<template>
  <div>
    <ok-cancel-modal
      :title="$t('variables.variableSelector')"
      :ok-button="$t('commons.ok')"
      :cancel-button="$t('commons.cancel')"
      v-on:ok="onOk()"
      v-on:cancel="onCancel()">
      <div
        slot="body">
        <search-input
          class="search-input"
          v-on:input="onChangeFilter"/>
        <div
          v-has-feature="'Taxonomy'">
          <h3>
            <font-awesome-icon
              :icon="icon.tags"/>
            {{ $t('variables.tags') }}
          </h3>
          <treeselect
            v-model="tags"
            @input="selectedTagsChanged"
            :multiple="true"
            :options="getTreeOptions"
            :valueConsistsOf="'LEAF_PRIORITY'"/>
        </div>
        <div
          class="margin-top-xl variable-list">
          <div class="select-all-wrapper"
            @click="onSelectAllChange">
            <checkbox
              :checked="verifyAllChecked"
              check-size="s"/>
            <span>{{ $t('variables.selection') }}</span>
          </div>
          <h3
            class="margin-bottom-l">
            <font-awesome-icon
              :icon="icon.variables"/>
            {{ $t('variables.variables') }}
          </h3>
          <checkbox
            v-for="(variable, index) in getFilteredVariables.slice(0, maxVariablesShown)"
            :key="index"
            v-on:change="onVariableSelected(variable)"
            :checked="isChecked(variable)"
            :id="String(variable.id)"
            :label="variable.label"/>
          <div
            v-if="getFilteredVariables.length > maxVariablesShown"
            class="and-x-more">
            {{ $t('variables.andXMore', {count: variablesToShow() })}}
          </div>
        </div>
      </div>
    </ok-cancel-modal>
  </div>
</template>

<script>
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import OkCancelModal from '@/components/shared/dialogs/OkCancelModal';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import {
  faTags,
  faChartBar
} from '@fortawesome/pro-regular-svg-icons';
import Checkbox from '@/components/shared/forms/Checkbox';
import SearchInput from '@/components/shared/SearchInput';

export default {
  name: 'VariableMultipleSelectorModal',
  components: {
    Checkbox,
    OkCancelModal,
    Treeselect,
    FontAwesomeIcon,
    SearchInput
  },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    taggableVariables: {
      type: Array,
      default: () => []
    },
    variables: {
      type: Array,
      default: () => []
    },
    taggedVariables: {
      type: Array,
      default: () => []
    },
    tree: {
      type: Array,
      default: () => []
    },
    selectedTags: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      tags: this.selectedTags,
      selectedVariables: this.value,
      selectedTaggableVariables: this.taggableVariables,
      icon: {
        tags: faTags,
        variables: faChartBar
      },
      allChecked: false,
      variableFilter: '',
      initialSelectedVariables: [],
      initialSelectedTaggableVariables: [],
      maxVariablesShown: 30
    };
  },
  created () {
    this.initialSelectedVariables = [...this.selectedVariables];
    this.initialSelectedTaggableVariables = [...this.selectedTaggableVariables];
  },
  computed: {
    getTreeOptions () {
      return createOptions(this.tree);
    },
    getFilteredVariables () {
      if (this.variableFilter !== '') {
        return this.taggedVariables.filter(variable => {
          return variable.label.toUpperCase().includes(this.variableFilter.toUpperCase());
        });
      }
      return this.taggedVariables;
    },
    verifyAllChecked () {
      const filteredArray = this.getFilteredVariables.map(filteredVar => filteredVar.id);
      return filteredArray.every(val => this.selectedVariables.includes(val));
    }
  },
  methods: {
    isChecked (variable) {
      return this.selectedVariables !== undefined &&
      this.selectedVariables.length > 0 &&
      this.selectedVariables.includes(variable.id);
    },
    onVariableSelected (variable) {
      if (this.selectedVariables.includes(variable.id)) {
        this.selectedVariables = this.selectedVariables.filter(arrayItem => arrayItem !== variable.id);
        this.selectedTaggableVariables = this.selectedTaggableVariables.filter(arrayItem => arrayItem !== variable);
      } else {
        this.selectedVariables.push(variable.id);
        this.selectedTaggableVariables.push(variable);
      }
    },
    onOk () {
      this.$emit('input', this.selectedVariables);
      this.$emit('selectedTaggableVariables', this.selectedTaggableVariables);
      this.$emit('close');
    },
    onCancel () {
      this.$emit('input', this.initialSelectedVariables);
      this.$emit('selectedTaggableVariables', this.initialSelectedTaggableVariables);
      this.$emit('close');
    },
    onSelectAllChange () {
      if (this.verifyAllChecked) {
        this.allChecked = false;
        this.getFilteredVariables.forEach(filteredVariable => {
          const filteredVariableIndex = this.selectedVariables.findIndex(selectedVar => selectedVar === filteredVariable.id);
          this.selectedVariables.splice(filteredVariableIndex, 1);
          this.selectedTaggableVariables.splice(filteredVariableIndex, 1);
        });
      } else {
        this.allChecked = true;
        this.getFilteredVariables.forEach(filteredVariable => {
          if (!this.selectedVariables.includes(filteredVariable.id)) {
            this.selectedVariables.push(filteredVariable.id);
            this.selectedTaggableVariables.push(filteredVariable);
          }
        });
      }
    },
    onChangeFilter (event) {
      this.variableFilter = event;
    },
    variablesToShow () {
      return this.getFilteredVariables.length - this.maxVariablesShown;
    },
    selectedTagsChanged (tags) {
      this.$emit('selectedTagsChanged', tags);
    }
  }
};

const createOptions = (nodes) => {
  const options = [];

  const sortedNodes = nodes.sort((a, b) => {
    return a.text.localeCompare(b.text, undefined, {numeric: true, sensitivity: 'base'});
  });

  for (let i = 0; i < sortedNodes.length; i++) {
    const option = {
      id: sortedNodes[i].data.id,
      label: sortedNodes[i].text
    };

    if (sortedNodes[i].children.length > 0) {
      option.children = createOptions(sortedNodes[i].children);
    }

    options.push(option);
  }

  return options;
};

  /*
   * With this property in <treeselect>:
   * value-consists-of="LEAF_PRIORITY"
   * is not neccessary fold logic
   */
const findFoldTags = (ids, nodes, parent = false) => {
  const tags = [];
  for (let i = 0; i < nodes.length; i++) {
    const id = ids.find(id => id === nodes[i].data.id);
    if (id || parent) {
      if (!nodes[i].children || nodes[i].children.length === 0) {
        tags.push({
          id: nodes[i].data.id,
          name: nodes[i].text
        });
      } else {
        const constChildTags = findFoldTags(ids, nodes[i].children, true);
        tags.push(...constChildTags);
      }
    } else if (nodes[i].children && nodes[i].children.length > 0) {
      const constChildTags = findFoldTags(ids, nodes[i].children, false);
      tags.push(...constChildTags);
    }
  }

  return tags;
};

</script>

<style lang="scss" scoped>
  @import "../../stylesheets/variables.scss";

  h3 {
    color: $jamaicol;
  }

  .variable-list {
    min-height: calc(100vh / 2);
    max-height: calc(100vh / 2);
    overflow-y: auto;
  }

  .select-all-wrapper {
    display: flex;
    align-items: center;
    margin-bottom: $sp-margin-small;
    cursor: pointer;
  }

  .search-input {
    margin-bottom: $sp-margin;
  }

  .and-x-more {
    color: $jamaicol;
  }
</style>



// WEBPACK FOOTER //
// src/components/variable/VariableMultipleSelectorModal.vue